import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const ExportDataPage = React.lazy(() => import("./ExportDataPage"));

/**
 * Data Export Module Config — "Export my data"
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (dataexport/permissions.ts)
 * and fetched via the modulePermissions GraphQL query. Arrays below ship EMPTY;
 * routes gate by `permissionLevel`.
 *
 * To change permissions: Edit business-builder-backend/src/modules/dataexport/permissions.ts
 */
const dataexportModuleConfig: ModuleConfig = {
  id: "dataexport",
  name: "Export my data",
  description: "Download any module's data as CSV, any time. No request, no waiting.",
  version: "1.0.0",
  enabled: true,
  category: "core",
  order: 98,
  icon: "FiDownload",
  color: "#2B6CB0",

  routes: [
    {
      path: "/settings/export-data",
      component: ExportDataPage,
      permissionLevel: "view",
      useLayout: true,
      layoutOptions: { maxW: "container.lg", py: 6 },
    },
  ],

  navigation: [
    {
      label: "Export my data",
      path: "/settings/export-data",
      icon: "📦",
      permissionLevel: "view",
    },
  ],

  // PERMISSIONS - resolved from backend at runtime
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  quickActions: [
    {
      label: "Export my data",
      path: "/settings/export-data",
      icon: "FiDownload",
      description: "Download any module as CSV, or everything at once",
    },
  ],

  changelog: [
    {
      version: "1.0.0",
      releaseDate: "2026-08-22",
      title: "Initial Release - Export my data",
      features: [
        "Manifest of every exportable dataset with live row counts",
        "Per-dataset CSV download (RFC 4180, UTF-8, CRLF)",
        "Export everything — sequential download of every dataset",
        "Honest list of modules not yet exportable",
        "First wave: clients, bills, calendar, employees, shifts, The Program, LeadFlow, Vibe Tribe",
      ],
      notes: "Your data is yours. Every download is on-demand and tenant-scoped; TFN and bank fields are redacted.",
    },
  ],

  roadmap: [
    {
      title: "Every module exportable",
      description: "Close the 'not yet exportable' list — one export.ts per remaining module.",
      priority: "high" as const,
      status: "in-progress" as const,
      features: ["Exporters for every registered module", "Attachments manifest (IPFS CIDs)"],
    },
    {
      title: "Single-file archive",
      description: "One click, one .zip containing every CSV plus a README.",
      priority: "medium" as const,
      status: "planned" as const,
      features: ["Zip download", "Export receipt"],
    },
  ],
};

export default dataexportModuleConfig;
